import React, { useMemo } from "react";

// Lightweight Gantt: 3-track horizontal bars with critical-path coloring and today marker.
// `methodology` reshapes the timeline overlay:
//  - "Waterfall" (default): sequential bars only.
//  - "Agile":  2-week sprint bands + sprint numeric labels above the axis.
//  - "Hybrid": phase bars + faded sprint band overlay.
export default function Gantt({ tasks, start, end, methodology = "Waterfall" }) {
  const ps = useMemo(() => new Date(start), [start]);
  const pe = useMemo(() => new Date(end), [end]);
  const total = pe - ps || 1;

  const months = useMemo(() => {
    const out = [];
    const d = new Date(ps.getFullYear(), ps.getMonth(), 1);
    const lastIso = pe.toISOString().slice(0,7);
    while (d.toISOString().slice(0,7) <= lastIso) {
      out.push({ label: d.toLocaleString("en", { month: "short" }) + " '" + (d.getFullYear() % 100).toString().padStart(2, "0"),
                 left: ((d - ps) / total) * 100 });
      d.setMonth(d.getMonth() + 1);
    }
    return out;
  }, [ps, pe, total]);

  // Two-week sprint bands anchored on the project start date. Agile shows them
  // as alternating filled bands; Hybrid overlays them faintly.
  const sprints = useMemo(() => {
    if (methodology === "Waterfall") return [];
    const out = [];
    const SPRINT_MS = 14 * 24 * 60 * 60 * 1000;
    let idx = 1;
    let cursor = ps.getTime();
    const endMs = pe.getTime();
    while (cursor < endMs) {
      const next = Math.min(cursor + SPRINT_MS, endMs);
      out.push({
        n: idx++,
        leftPct: ((cursor - ps.getTime()) / total) * 100,
        widthPct: ((next - cursor) / total) * 100,
      });
      cursor = next;
    }
    return out;
  }, [methodology, ps, pe, total]);

  const todayPct = Math.min(100, Math.max(0, ((new Date() - ps) / total) * 100));
  const tracks = [0, 1, 2];
  const bandOpacity = methodology === "Agile" ? 0.10 : 0.05;

  return (
    <div className="card overflow-hidden" data-testid={`gantt-${methodology.toLowerCase()}`}>
      <div className="flex items-center justify-between p-4 border-b border-[var(--border)] gap-3 flex-wrap">
        <div className="font-display text-base font-bold tracking-tight text-zinc-900">
          Timeline & Critical Path
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 ml-2">· {methodology}</span>
        </div>
        <div className="flex items-center gap-4 font-mono text-[10px] text-zinc-500 uppercase tracking-[0.1em]">
          <span className="flex items-center gap-1.5"><span className="w-3 h-[3px] rounded-sm bg-red-500/70"/> Critical</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-[3px] rounded-sm bg-blue-500/70"/> Standard</span>
          {sprints.length > 0 && (
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-cyan-500/25 border border-cyan-500/40"/> Sprint</span>
          )}
          <span className="flex items-center gap-1.5"><span className="w-[2px] h-3 bg-zinc-300"/> Today</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[760px]">
          {/* Month axis + optional sprint markers */}
          <div className="relative flex border-b border-[var(--border)] bg-white/40">
            <div className="flex-none w-[180px] border-r border-[var(--border)] p-2 font-mono text-[9px] uppercase tracking-[0.14em] text-zinc-500">
              {methodology === "Agile" ? "Sprint / Task" : "Task"}
            </div>
            <div className="flex-1 relative h-8">
              {months.map((m, i) => (
                <div key={i} className="absolute top-0 bottom-0 flex items-center font-mono text-[9px] text-zinc-500 uppercase tracking-[0.12em] pl-2 border-l border-[var(--border)]"
                     style={{ left: `${m.left}%` }}>{m.label}</div>
              ))}
              {methodology === "Agile" && sprints.map(sp => (
                <div key={`sp-${sp.n}`} className="absolute top-4 font-mono text-[8px] text-cyan-700 uppercase tracking-[0.1em] pl-1"
                     style={{ left: `${sp.leftPct}%`, width: `${sp.widthPct}%` }}
                     title={`Sprint ${sp.n}`}>S{sp.n}</div>
              ))}
            </div>
          </div>

          {/* Tracks */}
          <div className="relative">
            {/* Sprint band overlay (Agile/Hybrid) — behind bars */}
            {sprints.map(sp => (
              <div key={`sb-${sp.n}`}
                   className="absolute top-0 bottom-0 pointer-events-none"
                   style={{
                     left: `calc(180px + (100% - 180px) * ${sp.leftPct / 100})`,
                     width: `calc((100% - 180px) * ${sp.widthPct / 100})`,
                     background: sp.n % 2 === 0
                       ? `rgba(6, 182, 212, ${bandOpacity})`
                       : "transparent",
                     borderLeft: "1px dashed rgba(6, 182, 212, 0.3)",
                     zIndex: 0,
                   }} />
            ))}
            <div className="absolute top-0 bottom-0 z-10 pointer-events-none border-l border-dashed border-zinc-400/60"
                 style={{ left: `calc(180px + (100% - 180px) * ${todayPct / 100})` }} />

            {tracks.map(track => {
              const tt = tasks.filter(t => t.track === track);
              if (!tt.length) return null;
              return (
                <div key={track} className="flex border-b border-[var(--border)] min-h-[52px] relative z-[1]">
                  <div className="flex-none w-[180px] border-r border-[var(--border)] py-2 px-3 flex flex-col justify-center gap-1.5">
                    {tt.map(t => (
                      <div key={t.id} className="font-mono text-[10px] truncate" title={t.name}>
                        <span className={t.critical ? "text-red-700" : "text-zinc-500"}>{t.critical ? "▸ " : ""}</span>
                        <span className="text-zinc-700">{t.name}</span>
                      </div>
                    ))}
                  </div>
                  <div className="relative flex-1">
                    {tt.map((t, idx) => {
                      const ts = new Date(t.start), te = new Date(t.end);
                      const lp = Math.max(0, ((ts - ps) / total) * 100);
                      const wp = Math.min(((te - ts) / total) * 100, 100 - lp);
                      const done = t.pct || 0;
                      const top = 6 + idx * 18;
                      const baseBg = t.critical ? "rgba(239,68,68,0.15)" : "rgba(59,130,246,0.15)";
                      const fillBg = t.critical ? "rgba(239,68,68,0.45)" : "rgba(59,130,246,0.5)";
                      const border = t.critical ? "rgba(239,68,68,0.45)" : "rgba(59,130,246,0.4)";
                      return (
                        <div key={t.id}
                             title={`${t.name}: ${t.start} → ${t.end} (${done}%)`}
                             style={{ left: `${lp}%`, width: `${wp}%`, top, background: baseBg, border: `1px solid ${border}` }}
                             className="absolute h-[14px] rounded-sm overflow-hidden">
                          <div className="h-full bar-fill" style={{ width: `${done}%`, background: fillBg }} />
                          {done > 12 && (
                            <span className="absolute left-1.5 top-1/2 -translate-y-1/2 font-mono text-[9px]"
                                  style={{ color: t.critical ? "#b91c1c" : "#1d4ed8" }}>{done}%</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
